"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { FaClock, FaFacebookF, FaInstagram, FaGoogle, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";

const googleMapsUrl = "https://maps.google.com/maps?cid=8192593597492375843";
const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1040487013897!2d-73.91802349999999!3d40.7597362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25fc39318e9fb%3A0x71b1f0787fac3123!2ssmile4me%20Dental%20Care%20-%20Astoria%2C%20Queens!5e0!3m2!1sen!2sus!4v1755813645127!5m2!1sen!2sus";

const officeHours = [
  { day: "Monday", time: "10:00 am - 7:00 pm" },
  { day: "Tuesday", time: "10:00 am - 7:00 pm" },
  { day: "Wednesday", time: "10:00 am - 7:00 pm" },
  { day: "Thursday", time: "10:00 am - 7:00 pm" },
  { day: "Friday", time: "10:00 am - 2:00 pm" },
  { day: "Saturday", time: "Closed" },
  { day: "Sunday", time: "11:00 am - 5:00 pm" },
];

const Footer: React.FC = () => {
  return (
    <footer
      className="text-gray-700 py-8"
      role="contentinfo"
      aria-label="Smile4Me Footer"
    >
      <div className="container mx-auto px-6">
        {/* Awards */}
        <div className="mb-10 flex items-center justify-center gap-4 sm:gap-8">
          <Link
            href="https://todaysbestdentists.com/dentist/iosif-khaimov/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View Dr. Iosif Khaimov's America's Best Dentist 2026 profile"
            className="relative block"
            style={{
              width: "clamp(120px, 14vw, 180px)",
              height: "clamp(96px, 11vw, 142px)",
            }}
          >
            <Image
              src="/images/2026-DEN-OCTAGONAL-Blue.png"
              alt="America's Best Dentist 2026"
              fill
              className="object-contain"
              sizes="(max-width: 1024px) 170px, 170px"
            />
          </Link>
          <div
            className="relative"
            style={{
              width: "clamp(120px, 14vw, 180px)",
              height: "clamp(96px, 11vw, 142px)",
            }}
          >
            <Image
              src="/images/custom_images_invisalign2024b.webp"
              alt="Invisalign Provider 2024"
              fill
              className="object-contain"
              sizes="(max-width: 1024px) 170px, 170px"
            />
          </div>
        </div>

        <div
          className="grid grid-cols-1 items-start gap-10 text-center md:text-left lg:grid-cols-3"
          style={{ columnGap: "64px", rowGap: "40px" }}
        >
          <div className="order-last w-full lg:order-1">
            <h3 className="text-3xl font-semibold mb-4 after:content-[''] after:block after:w-full after:max-w-[300px] after:h-[3px] after:my-5 after:mx-auto md:after:mx-0 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
              Our Location
            </h3>
            <div className="h-64 w-full overflow-hidden rounded-lg shadow-md">
              <iframe
                src={googleMapsEmbedUrl}
                className="border-0"
                style={{ width: "100%", height: "100%" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map of Smile4Me Dental Care in Astoria, Queens"
              />
            </div>
            <Link
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center text-[#AE7D2F] transition-colors duration-300 hover:text-[#ECD05B]"
              aria-label="Open Smile4Me Dental Care in Google Maps"
            >
              <FaMapMarkerAlt className="mr-2 text-base" />
              Open in Google Maps
            </Link>
          </div>

        {/* Contact Us */}
        <div className="order-1 mb-6 md:mb-0 lg:order-2">
          <h3
            className="text-3xl font-semibold mb-4 after:content-[''] after:block after:w-[300px] after:h-[3px] after:my-5 after:mx-auto md:after:mx-0 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]"
          >
            Contact Us
          </h3>
          <address
            className="space-y-2 text-gray-800 not-italic"
            aria-label="Contact information for Smile4Me"
          >
            <p className="flex items-center justify-center md:justify-start font-semibold">
              <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-1 inline-flex items-center justify-center">
                <FaPhoneAlt className="text-white text-base" />
              </span>
              <span className="ml-2">Phone</span>
            </p>
            <p className="flex items-center justify-center md:justify-start">
              <Link
                href="tel:+17184004400"
                className="text-gray-800 hover:text-[#AE7D2F] transition-colors duration-300"
                aria-label="Call Smile4Me at (718) 400-4400"
              >
                (718) 400-4400
              </Link>
            </p>
            <p className="flex items-center justify-center md:justify-start font-semibold">
              <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-1 inline-flex items-center justify-center">
                <FaMapMarkerAlt className="text-white text-base" />
              </span>
              <span className="ml-2">Address</span>
            </p>
            <p className="flex items-center justify-center md:justify-start">
              <Link
                href="https://maps.app.goo.gl/GcexFskLt3MwefMR9"
                className="text-gray-800 hover:text-[#AE7D2F] transition-colors duration-300"
                aria-label="View Smile4Me location on Google Maps"
                target="_blank"
                rel="noopener noreferrer"
              >
                31-57 Steinway St, Queens, NY 11103
              </Link>
            </p>
            <p className="font-semibold">Social</p>
            <div className="flex gap-3 justify-center md:justify-start mt-2">
              <Link
                href="https://www.facebook.com/profile.php?id=100090912068381"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Facebook"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaFacebookF className="text-white text-base" />
                </span>
              </Link>
              <Link
                href="https://www.instagram.com/smile4mecare/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Instagram"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaInstagram className="text-white text-base" />
                </span>
              </Link>
              <Link
                href="https://maps.app.goo.gl/GcexFskLt3MwefMR9"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Google Business"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaGoogle className="text-white text-base" />
                </span>
              </Link>
            </div>
          </address>
        </div>

        {/* Office Hours */}
        <div className="order-3 lg:order-3">
          <h3 className="text-3xl font-semibold mb-4 after:content-[''] after:block after:w-full after:max-w-[300px] after:h-[3px] after:my-5 after:mx-auto md:after:mx-0 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
            Office Hours
          </h3>
          <div className="mx-auto max-w-[360px] space-y-2 md:mx-0">
            <p className="mb-3 flex items-center justify-center font-semibold md:justify-start">
              <span className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] p-1">
                <FaClock className="text-base text-white" />
              </span>
              <span className="ml-2">Weekly Schedule</span>
            </p>
            {officeHours.map(({ day, time }) => (
              <div
                key={day}
                className="flex items-center justify-between gap-6 border-b border-gray-200 pb-2 text-gray-800 last:border-0"
              >
                <span className="font-medium">{day}</span>
                <span className={time === "Closed" ? "font-medium text-red-600" : ""}>
                  {time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Links */}
        <nav
          className="order-2 border-t border-gray-200 pt-8 text-center lg:order-4 lg:col-span-3 lg:mt-2"
          aria-label="Footer navigation"
        >
          <h3 className="text-2xl font-semibold after:content-[''] after:block after:w-full after:max-w-[300px] after:h-[3px] after:my-5 after:mx-auto after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
            Navigation
          </h3>
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <li>
              <Link href="/" className="text-gray-700 transition-colors duration-300 hover:text-[#AE7D2F]">
                Home
              </Link>
            </li>
            <li>
              <Link href="/our-office" className="text-gray-700 transition-colors duration-300 hover:text-[#AE7D2F]">
                About Our Office
              </Link>
            </li>
            <li>
              <Link href="/services" className="text-gray-700 transition-colors duration-300 hover:text-[#AE7D2F]">
                Services
              </Link>
            </li>
            <li>
              <Link href="/blog" className="text-gray-700 transition-colors duration-300 hover:text-[#AE7D2F]">
                Blog
              </Link>
            </li>
            <li>
              <Link href="/contact" className="text-gray-700 transition-colors duration-300 hover:text-[#AE7D2F]">
                Contact
              </Link>
            </li>
          </ul>
        </nav>
        </div>
      </div>

      {/* Bottom Content */}
      <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col items-center text-center container mx-auto px-6">
        <p className="text-gray-700 text-base">
          <Link
            href="/privacy-policy/"
            className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
            aria-label="Privacy Policy page"
          >
            Privacy Policy
          </Link>
          {" | "}
          <Link
            href="/accessibility/"
            className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
            aria-label="Accessibility Policy page"
            target="_blank"
            rel="noopener noreferrer"
          >
            Accessibility Policy
          </Link>
        </p>
        <p className="text-gray-700 text-base mt-2">
          © 2024 smile4me. All Rights Reserved.
        </p>
        <p className="text-gray-700 text-base mt-2">
          <Link
            href="https://www.excaliburinteractive.org/"
            className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
            aria-label="Excalibur Interactive website"
            target="_blank"
            rel="noopener noreferrer"
          >
            Developed and Maintained by Excalibur Interactive
          </Link>
        </p>
        <p className="text-gray-700 text-base mt-2">
          Astoria Dentist | Dentist 11103 | Cosmetic Dentist in Astoria
        </p>
      </div>
    </footer>
  );
};

export default Footer;
